/** Grundeinstellungen der Kamera. Die Vorderkamera nur, solange kein Gerät ausgewählt ist. */
const VIDEO_BASE: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
};

const VIDEO: MediaTrackConstraints = { ...VIDEO_BASE, facingMode: 'user' };

const AUDIO: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const cleanups = new WeakMap<MediaStream, () => void>();

/** Gemerkte Geräte. null heißt: das entscheidet der Browser selbst. */
export interface DeviceChoice {
  cameraId: string | null;
  micId: string | null;
}

export interface DeviceOption {
  id: string;
  label: string;
}

function videoConstraints(cameraId: string | null): MediaTrackConstraints {
  return cameraId ? { ...VIDEO_BASE, deviceId: { exact: cameraId } } : VIDEO;
}

function audioConstraints(micId: string | null): MediaTrackConstraints {
  return micId ? { ...AUDIO, deviceId: { exact: micId } } : AUDIO;
}

export async function getCameraStream(choice?: DeviceChoice): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('mediaDevices nicht verfügbar');
  const cameraId = choice?.cameraId ?? null;
  const micId = choice?.micId ?? null;
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: videoConstraints(cameraId),
      audio: audioConstraints(micId),
    });
  } catch (error) {
    // Gemerktes Gerät ist nicht mehr da (anderer Rechner, abgezogenes Headset) – dann das übliche.
    if (cameraId === null && micId === null) throw error;
    return navigator.mediaDevices.getUserMedia({ video: VIDEO, audio: AUDIO });
  }
}

/** Eine einzelne Spur öffnen, um sie im laufenden Gespräch auszutauschen. */
export async function openTrack(
  kind: 'video' | 'audio',
  deviceId: string | null,
): Promise<MediaStreamTrack> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('mediaDevices nicht verfügbar');
  const stream = await navigator.mediaDevices.getUserMedia(
    kind === 'video'
      ? { video: videoConstraints(deviceId) }
      : { audio: audioConstraints(deviceId) },
  );
  const [track] = kind === 'video' ? stream.getVideoTracks() : stream.getAudioTracks();
  if (!track) throw new Error('Keine Spur erhalten');
  for (const other of stream.getTracks()) if (other !== track) other.stop();
  return track;
}

/** Kameras und Mikrofone mit Namen. Namen liefert der Browser erst nach einmaliger Erlaubnis. */
export async function listInputs(): Promise<{ cameras: DeviceOption[]; mics: DeviceOption[] }> {
  if (!navigator.mediaDevices?.enumerateDevices) return { cameras: [], mics: [] };
  const devices = await navigator.mediaDevices.enumerateDevices();
  const pick = (kind: MediaDeviceKind, fallback: string): DeviceOption[] =>
    devices
      .filter((device) => device.kind === kind && device.deviceId !== '')
      .map((device, index) => ({
        id: device.deviceId,
        label: device.label || `${fallback} ${index + 1}`,
      }));
  return { cameras: pick('videoinput', 'Kamera'), mics: pick('audioinput', 'Mikrofon') };
}

export function stopStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
  cleanups.get(stream)?.();
}

/** Testbild statt Kamera: ?fake=1 in der Adresse (nur Entwicklung). */
export function wantsTestStream(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).has('fake');
}

export function mediaErrorText(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      // Eingebettete Browser (Instagram, TikTok, App-Vorschauen) sperren die Kamera oft ganz.
      return 'Kamera oder Mikrofon sind blockiert. Erlaube den Zugriff über das Symbol in der Adressleiste. Läuft die Seite in einem eingebetteten Browser (z. B. in Instagram, TikTok oder einer App-Vorschau), öffne sie direkt in Safari, Chrome oder Firefox.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Keine Kamera oder kein Mikrofon gefunden.';
    case 'NotReadableError':
    case 'AbortError':
      return 'Die Kamera wird gerade von einer anderen App benutzt.';
    default:
      return window.isSecureContext
        ? 'Die Kamera konnte nicht gestartet werden.'
        : 'Die Kamera funktioniert nur über HTTPS (oder auf localhost).';
  }
}

/**
 * Künstlicher Kamera-Stream für Tests ohne Kamera (z. B. mehrere Tabs auf einem Rechner):
 * animierter Farbverlauf mit Beschriftung und eine stille Tonspur.
 */
export function createTestStream(label: string): MediaStream {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas nicht verfügbar');

  const hue = [...label].reduce((sum, char) => sum + char.charCodeAt(0) * 37, 0) % 360;
  const startedAt = performance.now();

  const draw = () => {
    const t = (performance.now() - startedAt) / 1_000;
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, `hsl(${(hue + t * 15) % 360} 65% 42%)`);
    gradient.addColorStop(1, `hsl(${(hue + 70 + t * 15) % 360} 70% 22%)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.beginPath();
    ctx.arc(320 + Math.cos(t * 1.3) * 190, 240 + Math.sin(t * 1.9) * 130, 70, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.font = '600 52px system-ui, sans-serif';
    ctx.fillText(label, 320, 255);
    ctx.font = '22px system-ui, sans-serif';
    ctx.fillText(`Testbild · ${new Date().toLocaleTimeString('de-DE')}`, 320, 440);
  };
  draw();
  const timer = window.setInterval(draw, 1_000 / 15);
  const stream = canvas.captureStream(15);

  const audio = new AudioContext();
  const destination = audio.createMediaStreamDestination();
  const silence = audio.createGain();
  silence.gain.value = 0;
  const oscillator = audio.createOscillator();
  oscillator.connect(silence).connect(destination);
  oscillator.start();
  for (const track of destination.stream.getAudioTracks()) stream.addTrack(track);

  cleanups.set(stream, () => {
    window.clearInterval(timer);
    oscillator.stop();
    void audio.close();
  });
  return stream;
}
